import * as Yup from "yup";
import { tableDef, blankRow, cleanTable, tableProblem } from "./TableInput";

/**
 * Pure helpers for FormBody: the ordered question list, the Formik initial values, the Yup
 * schema (SPEC 7.3) and the answers payload (SPEC 19.4). Kept apart so the component file
 * holds only the rendering and the submit flow.
 */

export const REQUIRED = "This answer is required";
export const CHOOSE_ONE = "Choose at least one";
export const SHORT_MAX = 255;
export const LONG_MAX = 4000;
export const RATING_VALUES = [1, 2, 3, 4, 5];
export const AGREE_REQUIRED = "Please confirm you agree before signing.";

// SPEC 23.5: on a per-person form the hr questions are shown read-only from the assignment's
// hr_values; the employee answers only the `filled_by = employee` ones.
export const isPerPerson = (item) => !!(item && item.per_person);
export const employeeQuestions = (item, questions) =>
  isPerPerson(item) ? questions.filter((q) => q.filled_by !== "hr") : questions;
export const hrQuestions = (item, questions) =>
  isPerPerson(item) ? questions.filter((q) => q.filled_by === "hr") : [];

// The assignment on screen: the one the queue chose (item.assignment), else the oldest pending.
export const currentAssignment = (item) => {
  if (!item) return null;
  if (item.assignment) return item.assignment;
  return Array.isArray(item.assignments) && item.assignments.length ? item.assignments[0] : null;
};

// Formik field name for a question (never a bare number: Formik's setIn treats those as indexes).
export const fieldName = (question) => "q" + question.id;

// The questions in position order whatever order they arrived in.
// SPEC 23.5 (audit fix 30 Oct): every pending assignment carries its own `form` {version, questions} for
// that assignment's version; a per-person document is rendered from it (item.form is the fallback).
export const formOf = (item) => {
  if (item && item.per_person) {
    const a = item.assignment || (Array.isArray(item.assignments) && item.assignments.length ? item.assignments[0] : null);
    if (a && a.form) return a.form;
  }
  return item ? item.form : null;
};

export const orderedQuestions = (item) => {
  const form = formOf(item);
  const list = form && Array.isArray(form.questions) ? form.questions.slice() : [];
  return list.sort((a, b) => Number(a.position) - Number(b.position));
};

// Every answer starts blank: "" for text-like inputs, null for picks, [] for multi.
export const initialValues = (questions) => {
  const values = {};
  questions.forEach((q) => {
    if (q.qtype === "multi") values[fieldName(q)] = [];
    else if (q.qtype === "table") values[fieldName(q)] = [blankRow(tableDef(q).columns)];
    else if (q.qtype === "rating" || q.qtype === "yes_no" || q.qtype === "single") values[fieldName(q)] = null;
    else values[fieldName(q)] = "";
  });
  return values;
};

// Blank = nothing to send (and a required question not answered). 0 is an answer (No).
export const isBlank = (value) =>
  value === null || value === undefined ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

const requiredTest = (message) => ({
  name: "required",
  message: message,
  test: (value) => !isBlank(value),
});

// One Yup rule per question. Formik hands "" to the schema as undefined, so every field is
// nullable and the required check is explicit rather than Yup's own.
export const buildSchema = (questions) => {
  const shape = {};
  questions.forEach((q) => {
    let rule;
    switch (q.qtype) {
      case "short_text":
        rule = Yup.string().nullable().max(SHORT_MAX, "Keep it under " + SHORT_MAX + " characters");
        break;
      case "long_text":
        rule = Yup.string().nullable().max(LONG_MAX, "Keep it under " + LONG_MAX + " characters");
        break;
      case "multi":
        rule = Yup.array().of(Yup.string());
        break;
      case "table":
        rule = Yup.mixed().test({ name: "table", test: function (value) {
          const problem = tableProblem(q, value);
          return problem ? this.createError({ message: problem }) : true;
        } });
        break;
      default:
        rule = Yup.mixed().nullable();
    }
    if (q.required && q.qtype !== "table") {
      rule = rule.test(requiredTest(q.qtype === "multi" ? CHOOSE_ONE : REQUIRED));
    }
    shape[fieldName(q)] = rule;
  });
  return Yup.object().shape(shape);
};

// The POST body (19.4): one typed entry per answered question in position order; unanswered
// optional questions are omitted (the server treats a missing optional as blank).
export const buildPayload = (item, questions, values, agree) => {
  const answers = [];
  questions.forEach((q) => {
    let value = values[fieldName(q)];
    if (q.qtype === "table") value = cleanTable(tableDef(q).columns, value);
    if (isBlank(value)) return;
    answers.push({ question_id: q.id, value: value });
  });
  // SPEC 23.6: a per-person body names the assignment; its period key is "A<id>" and its version
  // the assignment's. `agree` travels on every per-person body and on any signed form.
  if (isPerPerson(item)) {
    const a = currentAssignment(item) || {};
    return {
      form_version: a.form_version !== undefined ? a.form_version : (formOf(item) ? formOf(item).version : undefined),
      period_key: a.period_key || "A" + a.id,
      assignment_id: a.id,
      answers: answers,
      agree: !!agree,
    };
  }
  const extra = item.require_signature ? { agree: !!agree } : {};
  return {
    period_key: item.period_key === undefined || item.period_key === null ? "" : item.period_key,
    form_version: item.form ? item.form.version : undefined,
    answers: answers,
    ...extra,
  };
};
